import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TemplatesService } from '../templates.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-template-type-edit',
    templateUrl: './template-type-edit.component.html',
    styleUrls: ['./template-type-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TemplateTypeEditComponent implements OnInit, OnDestroy {
  loading = false;
  crudLoading = false;
  minimise = false;
  submitted = false;
  title = 'Add New Template Type';
  mode: 'new' | 'edit' = 'new';

  template_type_data: any = {
    template_type_key: '',
    template_type_name: '',
    template_type_description: '',
    template_type_active: 1,
  };

  private rowData: any = {};
  private destroy$ = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<TemplateTypeEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private templatesService: TemplatesService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {
    this.rowData = data?.rowData || {};
    this.mode = data?.mode || (this.rowData?.TEMPLATE_TYPE_KEY ? 'edit' : 'new');
  }

  ngOnInit(): void {
    if (this.mode === 'edit') {
      this.title = 'Update Template Type';
      this.loadTemplateTypeDetail();
    } else {
      this.title = 'Add New Template Type';
      this.template_type_data.template_type_active = 1;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadTemplateTypeDetail(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.templatesService.getTemplateTypeDetail(this.rowData.TEMPLATE_TYPE_KEY)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res?.callSuccess === '1' && res?.jsonObject?.[0]) {
            const obj = res.jsonObject[0];
            const lower: any = {};
            Object.keys(obj).forEach(k => lower[k.toLowerCase()] = obj[k]);
            this.template_type_data = lower;
          } else {
            this.snackbar.openSnackBar('Error loading template type data', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Error loading template type data', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  save(): void {
    this.submitted = true;
    this.cdr.detectChanges();

    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    if (!this.template_type_data.template_type_name?.trim()) {
      this.snackbar.openSnackBar('Template Type Name is required', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    let payload: any[];
    if (this.mode === 'edit') {
      payload = [{
        TEMPLATE_TYPE_KEY: this.template_type_data.template_type_key,
        TEMPLATE_TYPE_NAME: this.template_type_data.template_type_name,
        TEMPLATE_TYPE_DESC: this.template_type_data.template_type_description,
        IS_ACTIVE: this.template_type_data.template_type_active,
      }];
    } else {
      payload = [{
        TEMPLATE_TYPE_NAME: this.template_type_data.template_type_name,
        TEMPLATE_TYPE_DESC: this.template_type_data.template_type_description,
      }];
    }

    this.templatesService.saveTemplateType(payload, this.mode === 'edit')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Template Type Saved Successfully', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Save failed', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error saving template type', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  cancel(): void {
    this.dialogRef?.close();
  }
}
